import React, { useLayoutEffect, useRef } from "react";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";

import whyShouldImage from "../../assets/whyshould.png";

gsap.registerPlugin(ScrollTrigger);

export default function WhyYouShould() {
  const sectionRef = useRef(null);
  const imageRef = useRef(null);
  const reasonRefs = useRef([]);
  const chipRefs = useRef([]);
  const pathRef = useRef(null);

  const reasons = [
    {
      label: "Accuracy",
      title: "Attendance and payroll stay connected",
      text: "Leaves, half-days, overtime and deductions flow into payroll automatically.",
    },
    {
      label: "Trust",
      title: "Employees get salary clarity",
      text: "Payslips and salary proof are generated clearly, so employees do not ask repeatedly.",
    },
    {
      label: "Speed",
      title: "Monthly payroll becomes faster",
      text: "No more checking scattered notes, old messages or manual calculations.",
    },
    {
      label: "Control",
      title: "Owners can review before paying",
      text: "You can verify deductions, net salary and payslip details before final payroll.",
    },
  ];

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".wys-eyebrow, .wys-title, .wys-subtitle",
        {
          opacity: 0,
          y: 34,
        },
        {
          opacity: 1,
          y: 0,
          duration: 0.85,
          ease: "power3.out",
          stagger: 0.12,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 72%",
          },
        }
      );

      gsap.fromTo(
        ".wys-problem-card",
        {
          opacity: 0,
          x: -70,
          rotate: -2,
        },
        {
          opacity: 1,
          x: 0,
          rotate: 0,
          duration: 0.95,
          ease: "power4.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 58%",
          },
        }
      );

      gsap.fromTo(
        imageRef.current,
        {
          opacity: 0,
          scale: 0.9,
          rotate: 2,
          clipPath: "inset(12% 12% 12% 12% round 38px)",
        },
        {
          opacity: 1,
          scale: 1,
          rotate: 0,
          clipPath: "inset(0% 0% 0% 0% round 38px)",
          duration: 1.1,
          ease: "power4.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 52%",
          },
        }
      );

      gsap.fromTo(
        reasonRefs.current,
        {
          opacity: 0,
          y: 45,
          scale: 0.95,
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.75,
          ease: "power3.out",
          stagger: 0.12,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 42%",
          },
        }
      );

      gsap.fromTo(
        chipRefs.current,
        {
          opacity: 0,
          y: 28,
          scale: 0.85,
          rotate: -5,
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          rotate: 0,
          duration: 0.7,
          ease: "back.out(1.8)",
          stagger: 0.12,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 45%",
          },
        }
      );

      if (pathRef.current) {
        const length = pathRef.current.getTotalLength();

        gsap.set(pathRef.current, {
          strokeDasharray: length,
          strokeDashoffset: length,
        });

        gsap.to(pathRef.current, {
          strokeDashoffset: 0,
          ease: "none",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 65%",
            end: "bottom 52%",
            scrub: 1,
          },
        });
      }

      gsap.to(".wys-chip-float-a", {
        y: -14,
        rotate: 2.5,
        duration: 3,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });

      gsap.to(".wys-chip-float-b", {
        y: 14,
        rotate: -2.5,
        duration: 3.4,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} id="why-use-it" className="wys-section">
      <div className="wys-container">
        <div className="wys-header">
          <span className="wys-eyebrow">Why you should use it</span>

          <h2 className="wys-title">
            From payroll confusion to salary clarity.
          </h2>

          <p className="wys-subtitle">
            SlipSense helps small businesses stop guessing, calculate payroll
            accurately, and give employees clear salary proof every month.
          </p>
        </div>

        <div className="wys-layout">
          {/* LEFT STORY */}
          <div className="wys-left">
            <div className="wys-problem-card">
              <span>The real problem</span>

              <h3>
                Manual payroll creates mistakes, delays and repeated employee
                questions.
              </h3>

              <p>
                Salary, leave, half-days, deductions and payslips should not be
                managed from memory or messy notes.
              </p>
            </div>

            <div className="wys-result-strip">
              <div>
                <strong>Before</strong>
                <span>Confusing payroll</span>
              </div>

              <div>
                <strong>After</strong>
                <span>Clear salary proof</span>
              </div>
            </div>
          </div>

          {/* CENTER IMAGE */}
          <div className="wys-visual">
            <svg
              className="wys-orbit-line"
              viewBox="0 0 760 620"
              preserveAspectRatio="none"
            >
              <path
                ref={pathRef}
                d="M80 330 C180 70, 590 40, 690 285 C780 505, 470 640, 210 520 C55 448, 20 390, 80 330"
                fill="none"
                stroke="#cbd5e1"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>

            <div className="wys-image-card" ref={imageRef}>
              <img
                src={whyShouldImage}
                alt="SlipSense payroll clarity illustration"
              />
            </div>

            <div
              className="wys-floating-chip wys-chip-1 wys-chip-float-a"
              ref={(el) => (chipRefs.current[0] = el)}
            >
              <span>No guesswork</span>
              <strong>Salary is calculated clearly</strong>
            </div>

            <div
              className="wys-floating-chip wys-chip-2 wys-chip-float-b"
              ref={(el) => (chipRefs.current[1] = el)}
            >
              <span>Payslip proof</span>
              <strong>Employees stop asking repeatedly</strong>
            </div>

            <div
              className="wys-floating-chip wys-chip-3 wys-chip-float-a"
              ref={(el) => (chipRefs.current[2] = el)}
            >
              <span>Owner control</span>
              <strong>Review before final payroll</strong>
            </div>
          </div>
        </div>

        {/* BOTTOM BENEFITS */}
        <div className="wys-benefit-row">
          {reasons.map((item, index) => (
            <div
              className="wys-benefit-card"
              key={item.title}
              ref={(el) => (reasonRefs.current[index] = el)}
            >
              <span>{item.label}</span>
              <h4>{item.title}</h4>
              <p>{item.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}